import React, { createContext, useContext, useCallback, useRef } from 'react';
import {
  Toaster,
  useToastController,
  useId,
  Toast as FluentToast,
  ToastTitle,
  ToastBody,
  ToastIntent,
} from '@fluentui/react-components';

interface ToastContextValue {
  showToast: (message: string, intent?: ToastIntent) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  const showToast = useCallback((message: string, intent: ToastIntent = 'info') => {
    dispatchToast(
      <FluentToast>
        <ToastTitle>{message}</ToastTitle>
      </FluentToast>,
      { intent, position: 'top-end', timeout: 4000 }
    );
  }, [dispatchToast]);

  const success = useCallback((msg: string) => showToast(msg, 'success'), [showToast]);
  const error = useCallback((msg: string) => showToast(msg, 'error'), [showToast]);
  const warning = useCallback((msg: string) => showToast(msg, 'warning'), [showToast]);
  const info = useCallback((msg: string) => showToast(msg, 'info'), [showToast]);

  const value = { showToast, success, error, warning, info };

  return (
    <ToastContext.Provider value={value}>
      <Toaster toasterId={toasterId} />
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
